import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PaymentForm from "@/components/PaymentForm";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Crown, Coins, Banknote, Check, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ProActivate() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState("choose"); // choose | pay-sub | pay-pack
  const [pack, setPack] = useState("m");

  if (!user) return null;

  const paySub = async (card) => {
    const { data } = await api.post("/subscription/activate", card);
    setUser(data.user);
    toast.success("Abonnement Premium activé ! Bienvenue à bord.");
    nav("/pro/home", { replace: true });
  };

  const payPack = async (card) => {
    const { data } = await api.post("/wallet/buy-tokens", { pack, ...card });
    setUser(data.user);
    toast.success("Jetons crédités sur votre compte !");
    nav("/pro/home", { replace: true });
  };

  const PACKS = {
    s: { tokens: 5, price: "4,99" },
    m: { tokens: 15, price: "12,99" },
    l: { tokens: 40, price: "29,99" },
  };

  return (
    <AppShell role="pro" title="Activer mon compte">
      {step === "choose" && (
        <>
          <div className="text-center mb-5">
            <div className="h-14 w-14 rounded-2xl bg-pro text-white flex items-center justify-center mx-auto mb-3">
              <Crown className="w-7 h-7 text-brand" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-ink">Une dernière étape</h2>
            <p className="text-sm text-slate2 mt-2 leading-relaxed px-2">
              Choisissez votre mode de paiement pour recevoir les demandes de contact
              locales et pouvoir y répondre.
            </p>
          </div>

          {/* Premium */}
          <button onClick={() => setStep("pay-sub")} className="w-full bg-pro text-white rounded-2xl p-5 text-left tap-scale mb-3" data-testid="activate-sub-btn">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">Recommandé</div>
                <div className="font-heading font-bold text-xl">Premium · 9,99€/mois</div>
              </div>
              <Crown className="w-7 h-7 text-brand" />
            </div>
            <ul className="text-xs text-white/80 space-y-1">
              <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Demandes illimitées</li>
              <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> IA Pro coach business</li>
              <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Priorité sur les leads</li>
              <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Résiliable à tout moment</li>
            </ul>
          </button>

          {/* Packs jetons */}
          <div className="bg-white border border-border rounded-2xl p-4 mb-3">
            <div className="flex items-center gap-2 mb-3">
              <Coins className="w-4 h-4 text-brand" />
              <div className="font-heading font-bold text-ink">Pack de jetons</div>
              <span className="text-xs text-slate2 ml-auto">payez à l'usage</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {Object.entries(PACKS).map(([k, p]) => (
                <button
                  key={k}
                  onClick={() => setPack(k)}
                  className={`rounded-xl p-3 border tap-scale ${pack === k ? "bg-brand-light border-brand" : "bg-white border-border"}`}
                  data-testid={`activate-pack-${k}-btn`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-slate2">Pack {k.toUpperCase()}</div>
                  <div className="font-heading font-bold text-lg text-ink">{p.tokens} j</div>
                  <div className="text-xs text-brand font-semibold">{p.price} €</div>
                </button>
              ))}
            </div>
            <Button onClick={() => setStep("pay-pack")} className="w-full rounded-full bg-brand hover:bg-brand-hover" data-testid="activate-pack-continue">
              Continuer avec le pack {pack.toUpperCase()} · {PACKS[pack].price} €
            </Button>
          </div>

          {/* Pay-per-lead */}
          <div className="bg-bone rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="w-4 h-4 text-slate2" />
              <div className="font-heading font-bold text-sm text-ink">Pay-per-lead</div>
            </div>
            <p className="text-xs text-slate2 leading-relaxed">
              Pour un premier essai, achetez un pack S (4,99€) pour débloquer votre première demande à 3€.
              Aucune carte enregistrée avant paiement.
            </p>
          </div>

          <div className="text-center text-[11px] text-slate2 mt-4">
            En activant votre compte, vous acceptez la charte Besoin d&apos;un Pro.
          </div>
        </>
      )}

      {step === "pay-sub" && (
        <>
          <button onClick={() => setStep("choose")} className="flex items-center gap-1 text-sm text-slate2 mb-3" data-testid="pay-back-btn"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <PaymentForm amount="9,99 €" title="Abonnement Premium" subtitle="Facturé mensuellement · Résiliable" onPay={paySub} />
        </>
      )}
      {step === "pay-pack" && (
        <>
          <button onClick={() => setStep("choose")} className="flex items-center gap-1 text-sm text-slate2 mb-3" data-testid="pay-back-btn"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <PaymentForm amount={`${PACKS[pack].price} €`} title={`Pack ${pack.toUpperCase()}`} subtitle={`${PACKS[pack].tokens} jetons · paiement unique`} onPay={payPack} />
        </>
      )}
    </AppShell>
  );
}
